import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PredictAllModal from './PredictAllModal';
import { predictionsAPI } from '../services/predictionsApi';

/**
 * "Predict All" button for the top of a tournament schedule — predicts
 * every eligible match (both participants assigned) in one batch request
 * instead of clicking Predict on each match individually.
 *
 * Whether this sport actually supports predictions isn't known until
 * clicked — checked lazily here rather than the page prefetching
 * supported-sports data for every schedule view regardless of use.
 */
const PredictAllButton = ({ games, sportTypeId, sportName, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [checkingSupport, setCheckingSupport] = useState(false);

  const eligibleCount = games.filter(g => g.participant_a_id && g.participant_b_id).length;
  if (eligibleCount === 0) return null;

  const handleClick = async () => {
    setCheckingSupport(true);
    try {
      const supportedSports = await predictionsAPI.getSupportedSports();
      if (supportedSports?.kynaix?.includes(sportTypeId)) {
        setOpen(true);
      } else {
        toast.error(`Predictions aren't available for ${sportName || 'this sport'} yet.`);
      }
    } catch {
      toast.error('Could not check prediction support — please try again.');
    } finally {
      setCheckingSupport(false);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={checkingSupport}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white transition-colors ${className}`}
      >
        {checkingSupport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Prediction ({eligibleCount})
      </button>
      {open && (
        <PredictAllModal
          games={games}
          sportTypeId={sportTypeId}
          sportName={sportName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default PredictAllButton;
